import { useAtom } from "jotai";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { UserInfo } from "interfaces/user";
import Button from "components/base/Button";
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { faGoogle, faMicrosoft } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useUnauthorizedObserver from "hooks/useUnauthorizedObserver";
import EmailSettingHeader from "components/email/EmailSettingHeader";
import EmailSettingFooter from "components/email/EmailSettingFooter";
import EmailIntelligenceModal from "components/modals/EmailIntelligenceModal";
import { Badge, Col, Dropdown, Row, Card, Alert, Modal, Form } from "react-bootstrap";
import RevealDropdown, { RevealDropdownTrigger } from "components/base/RevealDropdown";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import {  
  faCog,
  faPlus, 
  faBolt,
  faTrash,
  faRocket,
  faCalendar,
  faEnvelope,
  faChartLine,
  faCheckCircle,
  faNetworkWired,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const EmailSetting = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("code");
  const microsoftConnected = searchParams.get("microsoft");
  
  // Helper function to get provider icon
  const getProviderIcon = (provider: string) => {
    if (provider === 'gmail' || provider === 'google') {
      return faGoogle;
    } else if (provider === 'microsoft' || provider === 'outlook') {
      return faMicrosoft;
    }
    return faEnvelope; // Default fallback
  };
  
  // Helper function to get provider gradient background
  const getProviderGradient = (provider: string) => {
    if (provider === 'gmail' || provider === 'google') {
      return 'linear-gradient(135deg, #ea4335 0%, #fbbc04 25%, #34a853 50%, #4285f4 100%)';
    } else if (provider === 'microsoft' || provider === 'outlook') {
      return 'linear-gradient(135deg, #0078d4 0%, #40a9ff 100%)';
    }
    return 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)'; // Default
  };

  const [connectedEmails, setConnectedEmails] = useState<any>([]);
  const [selectedEmailId, setSelectedEmailId] = useState<any>(null);
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [showEmailIntelligence, setShowEmailIntelligence] = useState(false);
  const [showCalendarSync, setShowCalendarSync] = useState(false);
  const [showAISettings, setShowAISettings] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<any>(null);

  const [userInfo] = useAtom<UserInfo>(user_info);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  useEffect(() => {
    if (code) {
      setLoading(true);
      
      const signupContext = localStorage.getItem('oauth_signup_context');
      
      if (signupContext) {
        const signupData = JSON.parse(signupContext);
        
        axiosInstance
          .post('/google/signup/process', {
            code,
            signup: "true",
            paid: signupData.paid?.toString() || "false",
            plan: signupData.plan || "basic",
            interval: signupData.interval || ""
          })
          .then((res) => {
            toast.success("Account created successfully! Welcome to KairosIQ!");
            
            localStorage.removeItem('oauth_signup_context');
            
            window.location.href = process.env.NODE_ENV === "development" 
              ? "http://localhost:3000/dashboard"
              : `${process.env.APP_URL}/dashboard`;
          })
          .catch((error) => {
            console.error("❌ [FRONTEND] Signup failed:", error);
            toast.error("Account creation failed. Please try again.");
            localStorage.removeItem('oauth_signup_context');
            
            // Redirect back to signup page
            window.location.href = process.env.NODE_ENV === "development" 
              ? "http://localhost:3000/sign-up"
              : `${process.env.APP_URL}/sign-up`;
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        // Also pass state parameter if present
        const state = searchParams.get("state");
        const params = new URLSearchParams({ code });
        if (state) params.append("state", state);
        
        // For OAuth callback, we need to handle authentication differently
        // since this is a redirect from Google and normal auth might be lost
        axiosInstance
          .get(`/google/oauth2callback?${params.toString()}`, {
            // Skip the normal auth interceptor for this request
            headers: {
              'X-OAuth-Callback': 'true'
            }
          })
          .then((res) => {
            toast.success("Gmail connected successfully!");
            return axiosInstance.get("/connected-emails");
          })
          .then((res) => {
            setConnectedEmails(res.data.data);
            ["code", "scope", "authuser", "prompt", "state"].forEach((param) =>
              searchParams.delete(param)
            );
            setSearchParams(searchParams);
          })
          .catch((error) => {
            toast.error("Failed to connect Gmail");
            console.error("OAuth callback failed:", error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [code, searchParams, setSearchParams]);

  // Handle Microsoft connection success
  useEffect(() => {
    if (microsoftConnected === "connected") {
      toast.success("Microsoft account connected successfully!");
      // Remove the microsoft parameter from URL
      searchParams.delete("microsoft");
      setSearchParams(searchParams);
    }
  }, [microsoftConnected, searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/connected-emails")
      .then((res) => {
        setConnectedEmails(res.data.data);
      })
      .catch((error) => {
        console.error("Failed to load connected emails:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useUnauthorizedObserver(isAuthorized);

  const handleDeleteEmail = async (id: string | number) => {
    try {
      await axiosInstance.delete(`/connected-emails/${id}`);
      toast.success("Email account disconnected successfully");
      setConnectedEmails(
        connectedEmails.filter((email: { id: string | number }) => email.id !== id)
      );
      setModalShow(false);
    } catch (error) {
      console.error("Failed to delete email:", error);
      toast.error("Failed to disconnect email account");
    } finally {
      setModalShow(false);
      setSelectedEmailId(null);
    }
  };

  return (
    <Fragment>
      <div style={{ minHeight: '100vh', marginTop: '-2rem' }}>
        <Row className="h-100 g-0">
          <Col xs={12} xl={12} xxl={10} className="mx-auto" style={{ marginTop: 0 }}>
            {/* AI-Enhanced Header */}
            <div 
              className="px-4"
              style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                border: '1px solid rgba(0, 212, 255, 0.2)',
                backdropFilter: 'blur(15px)',
                margin: '0',
                position: 'relative',
                overflow: 'hidden',
                padding: '1rem',
                marginBottom: '2rem',
                borderRadius: '0 0 16px 16px'
              }}
            >
              {/* Neural Network Background Effect */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d4ff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.3
                }}
              />
              
              <EmailSettingHeader />
            </div>

            {/* Main Content with AI Enhancement */}
            <Card 
              className="border-0 shadow-lg mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(0, 212, 255, 0.1) !important',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 212, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1) inset !important'
              }}
            >
              <Card.Header 
                className="border-bottom-0 p-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
                }}
              >
                <div className="p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={faGoogle} 
                        className="me-3"
                        style={{ 
                          color: '#00d4ff', 
                          fontSize: '24px',
                          filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.3))' 
                        }}
                      />
                      <div>
                        <h4 style={{ 
                          color: '#1f2937', 
                          fontWeight: 600, 
                          marginBottom: '4px',
                          fontSize: '1.3rem'
                        }}>
                          Connected Email Accounts
                        </h4>
                        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                          AI-powered email automation and intelligent lead management
                        </p>
                      </div>
                    </div>
                    
                    {/* Connection Status */}
                    <div className="d-flex align-items-center gap-3">
                      <Badge 
                        bg={connectedEmails.length > 0 ? 'success' : 'warning'}
                        className="px-3 py-2 d-flex align-items-center"
                        style={{ fontSize: '12px', fontWeight: 600 }}
                      >
                        <FontAwesomeIcon 
                          icon={connectedEmails.length > 0 ? faCheckCircle : faExclamationTriangle} 
                          className="me-2" 
                        />
                        {connectedEmails.length > 0 
                          ? `${connectedEmails.length} Account${connectedEmails.length !== 1 ? 's' : ''} Connected`
                          : 'No Accounts Connected'
                        }
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-4">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} />
                    <p className="text-muted">Loading email intelligence...</p>
                  </div>
                ) : connectedEmails.length === 0 ? (
                  /* No Connected Accounts State */
                  <div className="text-center py-5">
                    <div className="d-flex justify-content-center gap-4 mb-4">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: "80px", 
                          height: "80px",
                          background: getProviderGradient('gmail'),
                          boxShadow: '0 4px 15px rgba(66, 133, 244, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={faGoogle} 
                          className="text-white" 
                          style={{ fontSize: '32px' }}
                        />
                      </div>
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: "80px", 
                          height: "80px",
                          background: getProviderGradient('microsoft'),
                          boxShadow: '0 4px 15px rgba(0, 120, 212, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={faMicrosoft} 
                          className="text-white" 
                          style={{ fontSize: '32px' }}
                        />
                      </div>
                    </div>
                    <h5 className="mb-3" style={{ color: '#1f2937', fontWeight: 600 }}>
                      Connect Your Email Account
                    </h5>
                    <p className="text-muted mb-4" style={{ fontSize: '15px', maxWidth: '400px', margin: '0 auto' }}>
                      Enable AI-powered email automation, lead tracking, and intelligent response management by connecting your Gmail or Microsoft account.
                    </p>
                    <Button
                      to="/email-settings/new-email"
                      as={Link}
                      variant="primary"
                      size="lg"
                      className="px-4 py-3"
                      style={{
                        background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '14px',
                        boxShadow: '0 8px 25px rgba(0, 212, 255, 0.3)'
                      }}
                      startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                    >
                      Connect Email Account
                    </Button>
                  </div>
                ) : (
                  /* Connected Accounts Display */
                  <div>
                                         {/* AI Automation Status */}
                     <Alert 
                       variant="info" 
                       className="mb-4 border-0"
                       style={{
                         background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
                         borderRadius: '16px',
                         border: '1px solid rgba(59, 130, 246, 0.3)'
                       }}
                     >
                       <div className="d-flex align-items-center">
                         <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: '#2563eb' }} />
                         <div>
                           <strong style={{ color: '#1e40af' }}>Email Synchronization Active</strong>
                           <div className="small mt-1" style={{ color: '#1e3a8a' }}>
                             Your emails are being synchronized with your CRM leads. New emails are checked every few minutes.
                           </div>
                         </div>
                       </div>
                     </Alert>

                    {/* Connected Email Cards */}
                    <Row className="g-4">
                      {connectedEmails.map((email: any) => (
                        <Col md={6} key={email.id}>
                          <Card 
                            className="h-100 border-0"
                            style={{
                              background: 'rgba(255, 255, 255, 0.8)',
                              borderRadius: '16px',
                              border: '1px solid rgba(0, 212, 255, 0.1)',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 212, 255, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <Card.Body className="p-4">
                              <div className="d-flex align-items-start justify-content-between mb-3">
                                <div className="d-flex align-items-center">
                                  <div 
                                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{ 
                                      width: "48px", 
                                      height: "48px",
                                      background: getProviderGradient(email.provider),
                                      boxShadow: email.provider === 'microsoft' || email.provider === 'outlook' 
                                        ? '0 4px 15px rgba(0, 120, 212, 0.3)'
                                        : '0 4px 15px rgba(66, 133, 244, 0.3)'
                                    }}
                                  >
                                    <FontAwesomeIcon 
                                      icon={getProviderIcon(email.provider)} 
                                      className="text-white" 
                                      size="lg"
                                    />
                                  </div>
                                  <div>
                                    <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937' }}>
                                      {email.email}
                                    </h6>
                                    <div className="d-flex align-items-center">
                                      <Badge 
                                        bg="primary" 
                                        className="me-2"
                                        style={{ 
                                          fontSize: '10px',
                                          padding: '4px 8px',
                                          borderRadius: '8px'
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                        CONNECTED
                                      </Badge>
                                      <small className="text-muted">Email sync enabled</small>
                                    </div>
                                  </div>
                                </div>
                                
                                                                 <RevealDropdownTrigger>
                                   <RevealDropdown>
                                     <Dropdown.Item 
                                       eventKey="1" 
                                       className="d-flex align-items-center"
                                       onClick={() => {
                                         toast("Email details feature coming soon!", { icon: '📧' });
                                       }}
                                     >
                                       <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                                       Email Details
                                     </Dropdown.Item>
                                     <Dropdown.Item 
                                       eventKey="2" 
                                       className="d-flex align-items-center"
                                       onClick={() => {
                                         toast("Calendar sync feature coming soon!", { icon: '📅' });
                                       }}
                                     >
                                       <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                       Calendar Sync (Coming Soon)
                                     </Dropdown.Item>
                                     <Dropdown.Item 
                                       eventKey="3" 
                                       className="d-flex align-items-center"
                                       onClick={() => {
                                         toast("Sync settings feature coming soon!", { icon: '⚙️' });
                                       }}
                                     >
                                       <FontAwesomeIcon icon={faCog} className="me-2" />
                                       Sync Settings
                                     </Dropdown.Item>
                                     <Dropdown.Divider />
                                     <Dropdown.Item
                                       eventKey="4"
                                       className="text-danger d-flex align-items-center"
                                       onClick={() => {
                                         setModalShow(true);
                                         setSelectedEmailId(email.id);
                                       }}
                                     >
                                       <FontAwesomeIcon icon={faTrash} className="me-2" />
                                       Disconnect Account
                                     </Dropdown.Item>
                                   </RevealDropdown>
                                 </RevealDropdownTrigger>
                              </div>

                              {/* Connection Status */}
                              <div className="mt-3 pt-3 border-top border-light">
                                <Row className="g-3">
                                  <Col xs={6}>
                                    <div className="text-center">
                                      <FontAwesomeIcon icon={faEnvelope} className="text-primary mb-1" />
                                      <div className="fw-bold text-primary">Active</div>
                                      <small className="text-muted">Connection</small>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="text-center">
                                      <FontAwesomeIcon icon={faCalendar} className="text-info mb-1" />
                                      <div className="fw-bold text-info">Periodic</div>
                                      <small className="text-muted">Sync Schedule</small>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                      { connectedEmails?.length <= 0 &&                     <div className="text-center mt-4 pt-4 border-top border-light">
                      <Button
                        to="/email-settings/new-email"
                        as={Link}
                        variant="outline-primary"
                        size="lg"
                        className="px-4 py-2"
                        style={{
                          borderRadius: '12px',
                          borderColor: 'rgba(0, 212, 255, 0.3)',
                          color: '#00d4ff',
                          fontWeight: 500
                        }}
                        startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                      >
                        Connect Additional Account
                      </Button>
                    </div>}
                    {/* Add Another Account Option */}

                  </div>
                )}
              </Card.Body>
            </Card>

            <EmailSettingFooter />
          </Col>
        </Row>
      </div>

      {/* Enhanced Delete Confirmation Modal */}
      <ConfirmDeleteModal
        modalShow={modalShow}
        setModalShow={setModalShow}
        handleDelete={() => handleDeleteEmail(selectedEmailId)}
      />

      {/* Email Intelligence Modal */}
      <EmailIntelligenceModal
        show={showEmailIntelligence}
        onHide={() => setShowEmailIntelligence(false)}
        selectedEmail={selectedEmail}
      />

      {/* Calendar Sync Modal */}
      <Modal 
        show={showCalendarSync} 
        onHide={() => setShowCalendarSync(false)}
        size="lg"
        centered
      >
        <Modal.Header 
          closeButton
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            border: 'none'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-success" />
            Calendar Sync Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          <div className="text-center mb-4">
            <FontAwesomeIcon 
              icon={faCalendar} 
              size="3x" 
              className="text-success mb-3"
            />
            <h5>Smart Calendar Integration</h5>
            <p className="text-muted">
              Sync with: <strong>{selectedEmail?.email}</strong>
            </p>
          </div>
          
          <Form>
            <div className="mb-4">
              <h6>Synchronization Options</h6>
              <Form.Check
                type="switch"
                id="auto-sync-events"
                label="Auto-sync calendar events"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="ai-meeting-detection"
                label="AI-powered meeting detection"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="follow-up-reminders"
                label="Automatic follow-up reminders"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="conflict-detection"
                label="Smart conflict detection"
                defaultChecked
              />
            </div>
            
            <Alert variant="success" className="mb-0">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Calendar sync is active and processing events in real-time.
            </Alert>
          </Form>
        </Modal.Body>
      </Modal>

      {/* AI Settings Modal */}
      <Modal 
        show={showAISettings} 
        onHide={() => setShowAISettings(false)}
        size="lg"
        centered
      >
        <Modal.Header 
          closeButton
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            border: 'none'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2 text-info" />
            AI Intelligence Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          <div className="text-center mb-4">
            <FontAwesomeIcon 
              icon={faBolt} 
              size="3x" 
              className="text-info mb-3"
              style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))' }}
            />
            <h5>Neural Network Configuration</h5>
            <p className="text-muted">
              Account: <strong>{selectedEmail?.email}</strong>
            </p>
          </div>
          
          <Form>
            <div className="mb-4">
              <h6 className="mb-3">AI Processing Settings</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Lead Detection Sensitivity</Form.Label>
                <Form.Range 
                  min={1} 
                  max={10} 
                  defaultValue={8}
                  className="mb-2"
                />
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Conservative</small>
                  <small className="text-muted">Aggressive</small>
                </div>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Response Automation Level</Form.Label>
                <Form.Select defaultValue="medium">
                  <option value="low">Manual Review Required</option>
                  <option value="medium">Smart Suggestions</option>
                  <option value="high">Full Automation</option>
                </Form.Select>
              </Form.Group>
              
              <div className="mb-3">
                <h6>AI Features</h6>
                <Form.Check
                  type="switch"
                  id="sentiment-analysis"
                  label="Email sentiment analysis"
                  defaultChecked
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="priority-scoring"
                  label="Intelligent priority scoring"
                  defaultChecked
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="auto-categorization"
                  label="Automatic email categorization"
                  defaultChecked
                  className="mb-2"
                />
                <Form.Check
                  type="switch"
                  id="lead-scoring"
                  label="AI lead scoring"
                  defaultChecked
                />
              </div>
            </div>
            
            <Alert variant="info" className="mb-0">
              <FontAwesomeIcon icon={faBolt} className="me-2" />
              AI neural network is continuously learning from your email patterns to improve accuracy.
            </Alert>
          </Form>
        </Modal.Body>
      </Modal>
      
      {!isAuthorized && <Backdrop />}

      {/* AI Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          .ai-transition {
            transition: all 0.3s ease;
          }
          
          .ai-glow:hover {
            box-shadow: 0 0 30px rgba(0, 212, 255, 0.4) !important;
          }
        `
      }} />
    </Fragment>
  );
};

export default EmailSetting;
